/**
 * A live before its room opens, as the confirmed prototype draws it (g-front, "the day before"): "You're in", when
 * it is in the guest's own words and the hours left, their own time, the calendar, tonight's agenda, and the host's
 * private chat beside it. The Room tab waits, greyed, for the minute the room opens.
 */
import { type ReactNode, useState } from "react";
import type { LiveCalendarLinks, PublicBrand, WatchSession } from "../../../shared/types";
import { EMPTY_CHAT } from "../../live/room/chat";
import { Brand, Toast, useToast } from "../room/chrome";
import { GIcon } from "../room/GIcon";
import { useMediaQuery } from "../smallScreen";
import type { WatchThread } from "../WatchPageView";
import { type ColumnTab, LiveColumn } from "./LiveColumn";

/** "27:19:58": hours (as many as there are), minutes and seconds to the start. */
export function earlyClock(msLeft: number): string {
  const left = Math.max(0, Math.ceil(msLeft / 1000));
  const h = Math.floor(left / 3600);
  const m = Math.floor(left / 60) % 60;
  const s = left % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/** Google, Apple and Outlook, each marked once used; a series subscribes instead of saving one day. */
export function CalendarButtons({ links, onUsed }: { links: LiveCalendarLinks; onUsed: (which: string) => void }): ReactNode {
  const [used, setUsed] = useState<Record<string, boolean>>({});
  const mark = (which: string) => {
    setUsed((u) => ({ ...u, [which]: true }));
    onUsed(which);
  };
  const file = links.subscribe ?? links.ics;
  const button = (which: string, words: string, href: string, outside?: boolean) => (
    <a
      className={used[which] ? "btn btn-g done" : "btn btn-g"}
      href={href}
      {...(outside ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      onClick={() => mark(which)}
    >
      <GIcon name={used[which] ? "check" : "cal"} size={18} /> {words}
    </a>
  );
  return (
    <div className="cal">
      {button("google", "Google", links.google, true)}
      {button("apple", "Apple", file)}
      {button("outlook", "Outlook", file)}
    </div>
  );
}

interface EarlyProps {
  brand: PublicBrand;
  session: WatchSession;
  title: string;
  sub: string;
  hostFirst: string;
  viewer: { first: string; avatar: string | null };
  /** "You're in, Priya. Every Tuesday at 6:00 PM ET". */
  welcome: string;
  /** "Tomorrow, 6:00 PM". */
  day: string;
  clock: string;
  /** "Tue Nov 3 at 6:00 PM, your time. This page turns into the live when Maya starts." */
  note: string;
  calendar: LiveCalendarLinks | undefined;
  /** A series' rhythm in a calendar that keeps itself up to date. */
  subscribeWords: string | null;
  agenda: string[];
  opensAt: string;
  thread: WatchThread;
  unreadHost: boolean;
  onReadHost: () => void;
  yourPage: string;
}

export function LiveEarly(p: EarlyProps): ReactNode {
  const wide = useMediaQuery("(min-width:1024px)");
  const phone = useMediaQuery("(max-width:719px)");
  const [colOpen, setColOpen] = useState(wide);
  const [tab, setTab] = useState<ColumnTab>("maya");
  const { toast, say } = useToast();
  const pic = p.session.poster_url;
  const openCol = () => {
    setColOpen(true);
    setTab("maya");
    p.onReadHost();
  };
  return (
    <div className={["lv", "wait", "early", wide && !colOpen ? "col-off" : "", phone && colOpen ? "col-on" : ""].filter(Boolean).join(" ")}>
      <div className="amb-wrap">{pic ? <img className="amb" src={pic} alt="" aria-hidden="true" /> : null}</div>
      <div className="lv-main">
        <header className="lv-top">
          <Brand brand={p.brand} href={p.yourPage} />
          <span className="lv-tt" style={{ marginLeft: 6 }}>
            <b>{p.title}</b>
            <span>{p.sub}</span>
          </span>
          <a className="lv-badge soon" href={p.yourPage}>
            <GIcon name="home" size={15} />
            <span>Your page</span>
          </a>
        </header>
        <div className="lv-stage">
          <div className="lv-pic">
            <div className="curtain">
              <div className="curtain-pic" style={pic ? { backgroundImage: `url("${pic}")` } : undefined} />
              <p className="eyebrow">{p.welcome}</p>
              <p className="early-when">{p.day}</p>
              <div className="count early-count tnum" role="timer" aria-live="off">
                {p.clock}
              </div>
              <p className="sub">{p.note}</p>
              {p.calendar ? (
                <div className="early-cal">
                  <CalendarButtons
                    links={p.calendar}
                    onUsed={(which) =>
                      say(which === "google" ? "Google Calendar opened in a new tab with the live filled in." : "Saved to your calendar file. Open it to add the live.", "cal")
                    }
                  />
                  {p.calendar.subscribe && p.subscribeWords ? (
                    <a className="btn-q" href={p.calendar.subscribe} onClick={() => say("Every session is in your calendar, and it keeps itself up to date.", "cal")}>
                      <GIcon name="cal" size={16} /> {p.subscribeWords}
                    </a>
                  ) : null}
                </div>
              ) : null}
              {p.agenda.length ? (
                <ol className="tonight">
                  {p.agenda.map((t, i) => (
                    <li key={t}>
                      <span>{i + 1}</span>
                      {t}
                    </li>
                  ))}
                </ol>
              ) : null}
            </div>
          </div>
        </div>
        <div className="lv-dock early-dock">
          <button className="db chatb wide-ok" type="button" onClick={openCol}>
            <GIcon name="chat" size={22} />
            <span className="db-t">{p.hostFirst}</span>
          </button>
          <button className="btn btn-p early-join" type="button" disabled>
            <GIcon name="door" size={18} /> Join the room · opens {p.opensAt}
          </button>
        </div>
      </div>
      <aside className="lv-col" aria-label="Before the live">
        <LiveColumn
          tab={tab}
          onTab={(t) => {
            if (t === "room") {
              say(`The room opens at ${p.opensAt}. ${p.hostFirst}’s chat is open now.`, "clock");
              return;
            }
            setTab(t);
            if (t === "maya") p.onReadHost();
          }}
          stage="early"
          wide={wide}
          onTuck={() => setColOpen(false)}
          session={p.session}
          hostFirst={p.hostFirst}
          viewer={p.viewer}
          chat={EMPTY_CHAT}
          unreadRoom={0}
          unreadHost={p.unreadHost}
          opensAt={p.opensAt}
          liveAt={null}
          ended={false}
          reconnecting={false}
          send={() => false}
          faces={[]}
          speakingId={null}
          myPlace={0}
          onHand={() => {}}
          thread={p.thread}
          find=""
          onFind={() => {}}
        />
      </aside>
      {phone && colOpen ? <div className="scrim on" role="presentation" onClick={() => setColOpen(false)} /> : null}
      <button className="lv-colpill" type="button" onClick={openCol}>
        <GIcon name="untuck" size={18} /> Chat with {p.hostFirst}
      </button>
      <Toast toast={toast} />
    </div>
  );
}
